import { Logger, ReportException, reportException, SentryTags } from '@gitbook/util-debug';
import { ConnectionPool } from '@gitbook/util-websocket';

import { trackDataSync } from './datasynctracker';
import type { HiveClient } from './HiveClient';
import type { HiveDataSyncBrowserCache } from './HiveDataSyncBrowserCache';
import type { MessageSyncError } from './HiveDataSyncClient';
import {
    getHiveWorkingBranchGuid,
    HiveWorkingBranch,
    type HiveWorkingBranchOptions,
} from './HiveWorkingBranch';
import {
    getHiveWorkingDocumentGuid,
    HiveWorkingDocument,
    type HiveWorkingDocumentOptions,
} from './HiveWorkingDocument';

interface GetDataSyncOptions {
    /**
     * Automatically initialize the data sync client.
     * @default true
     */
    autoInit?: boolean;

    /**
     * Indicates the data sync should be read-only.
     * @default false
     */
    readOnly?: boolean;

    /**
     * A cache to use with the datasync.
     */
    cache?: HiveDataSyncBrowserCache;
}

const logger = Logger('hive:datasync-manager');

type ReadOnlyLock = {
    isAffectedSync: (sync: HiveWorkingDocument | HiveWorkingBranch) => boolean;
    syncs: WeakMap<HiveWorkingDocument | HiveWorkingBranch, boolean>;
};

/**
 * Manage the lifecycle of datasync clients.
 */
export class HiveDataSyncManager {
    public readonly client: HiveClient;

    private pool: ConnectionPool<HiveWorkingDocument | HiveWorkingBranch>;

    /**
     * The client ID used for data sync clients.
     * The ID doesn't represent the user, but represents the device / tab.
     *
     * The ID is allocated by the Hive server for the first data sync opened,
     * in case of conflicts, some data sync might get a different client ID allocated.
     */
    public clientId: string | null = null;

    /**
     * Cache the data sync Yjs doc in memory. It's optional.
     */
    private cache: HiveDataSyncBrowserCache | undefined = undefined;

    private readOnlyLocks: Set<ReadOnlyLock> = new Set();

    constructor(
        client: HiveClient,
        options: {
            /**
             * Time to live for data sync clients when inactive.
             * @default 5min
             */
            ttl?: number;

            /**
             * Optionally provide a browser cache.
             */
            dataSyncCache?: HiveDataSyncBrowserCache;
        } = {}
    ) {
        this.client = client;
        this.cache = options.dataSyncCache ?? undefined;

        this.pool = new ConnectionPool<HiveWorkingDocument | HiveWorkingBranch>({
            ttl: options.ttl ?? 1000 * 60 * 5,
            onDestroy: (sync) => sync.destroy(),
        });
    }

    /**
     * Clear the cache.
     */
    public async clearCache(): Promise<void> {
        await this.cache?.clear();
    }

    /**
     * Close a specific sync client.
     * This method is equivalent to calling `sync.close()`; it'll then call `sync.destroy()`.
     */
    public close(sync: HiveWorkingDocument | HiveWorkingBranch) {
        this.pool.delete(sync);
    }

    /**
     * Close all connections, trying to flush all pending dataSync first.
     */
    public async closeAll() {
        // Wait for all dataSync to be processed before closing them
        await Promise.all(
            this.pool.getAll().map(async (sync) => {
                await sync.waitForSync();
            })
        )
            // We report to Sentry but prevent crashing the app since there's no point in doing so
            .catch((error) => {
                logger.error('Error while closing all syncs', error);
                reportException(error);
            });

        this.pool.deleteAll();
    }

    /**
     * Mark a sync client as still active.
     */
    public touch(sync: HiveWorkingDocument | HiveWorkingBranch): boolean {
        return this.pool.touch(sync);
    }

    /**
     * Keep a sync client alive until the cleanup callback is called.
     * The sync client is not closed when the cleanup callback is called, but can be garbage collected.
     */
    public keepAlive(sync: HiveWorkingDocument | HiveWorkingBranch): () => void {
        return this.pool.keepAlive(sync);
    }

    /**
     * Run an operation across all data syncs, setting all affected syncs to read-only
     * whilst the operation is running.
     *
     * The operation will not run if we are unable to set data syncs to read-only.
     */
    public async withReadOnlyDataSyncs<T>(
        operation: () => Promise<T>,

        /**
         * Provide a function to determine which syncs should be set to
         * read-only during the operation.
         */
        isAffectedSync: (sync: HiveWorkingDocument | HiveWorkingBranch) => boolean
    ): Promise<T> {
        const lock: ReadOnlyLock = { isAffectedSync, syncs: new WeakMap() };
        this.readOnlyLocks.add(lock);

        const promises: Array<Promise<MessageSyncError[]>> = [];

        for (const sync of this.pool.getAll()) {
            // Set all affected syncs to read-only: if they are currently not read-only and if they pass the test
            if (!sync.dataSync.readOnly && isAffectedSync(sync)) {
                lock.syncs.set(sync, sync.dataSync.readOnly);
                promises.push(sync.dataSync.setReadOnly(true));
            }
        }

        try {
            const errors = (await Promise.all(promises)).flat();

            if (errors.length) {
                throw ReportException.annotateError(
                    new Error('Cannot set data-syncs to read-only'),
                    {
                        tags: {
                            [SentryTags.ERROR_CATEGORY]: 'data-sync-flush',
                        },
                        attachments: [
                            {
                                name: 'logs.txt',
                                body: errors.map((error) => error.toString()).join('\n'),
                            },
                        ],
                    }
                );
            }

            return await operation();
        } finally {
            this.readOnlyLocks.delete(lock);

            // Reset the data syncs back to their original states.
            for (const sync of this.pool.getAll()) {
                const readOnly = lock.syncs.get(sync);
                // The sync is affected by the current lock but not by any other lock
                if (
                    readOnly !== undefined &&
                    !Array.from(this.readOnlyLocks).some((l) => l.syncs.has(sync))
                ) {
                    await sync.dataSync.setReadOnly(readOnly);
                }
            }
        }
    }

    /**
     * Get a `HiveWorkingBranch` instance for a given content and branch.
     */
    public branch(
        contentId: string,
        branchId: string,
        options?: HiveWorkingBranchOptions
    ): HiveWorkingBranch;
    public branch(
        contentId: string,
        branchId: string,
        options?: GetDataSyncOptions & HiveWorkingBranchOptions
    ): HiveWorkingBranch | undefined;
    public branch(
        contentId: string,
        branchId: string,
        options?: GetDataSyncOptions & HiveWorkingBranchOptions
    ): HiveWorkingBranch | undefined {
        const branch = this.getSync(
            getHiveWorkingBranchGuid({ contentId, branchId }),
            (guid, options) => new HiveWorkingBranch(this, guid, { contentId, branchId }, options),
            { ...options, cache: this.cache }
        );

        if (branch && !(branch instanceof HiveWorkingBranch)) {
            throw new Error(`Expected HiveWorkingBranch, got ${branch.constructor.name}`);
        }

        return branch;
    }

    /**
     * Get a `HiveWorkingDocument` instance for a given page in a content branch.
     */
    public pageDocument(contentId: string, branchId: string, pageId: string): HiveWorkingDocument;
    public pageDocument(
        contentId: string,
        branchId: string,
        pageId: string,
        options: HiveWorkingDocumentOptions
    ): HiveWorkingDocument;
    public pageDocument(
        contentId: string,
        branchId: string,
        pageId: string,
        options?: GetDataSyncOptions & HiveWorkingDocumentOptions
    ): HiveWorkingDocument | undefined;
    public pageDocument(
        contentId: string,
        branchId: string,
        pageId: string,
        options?: GetDataSyncOptions & HiveWorkingDocumentOptions
    ): HiveWorkingDocument | undefined {
        const page = this.getSync(
            getHiveWorkingDocumentGuid({
                contentId,
                branchId,
                containerType: 'pages',
                containerId: pageId,
            }),
            (guid, options) =>
                new HiveWorkingDocument(
                    this,
                    guid,
                    { contentId, branchId, containerType: 'pages', containerId: pageId },
                    options
                ),
            { ...options, cache: this.cache }
        );

        if (page && !(page instanceof HiveWorkingDocument)) {
            throw new Error(`Expected HiveWorkingDocument, got ${page.constructor.name}`);
        }

        return page;
    }

    /**
     * Get a `HiveWorkingDocument` instance for reusable content.
     */
    public reusableContent(
        contentId: string,
        branchId: string,
        reusableContentId: string
    ): HiveWorkingDocument;
    public reusableContent(
        contentId: string,
        branchId: string,
        reusableContentId: string,
        options: HiveWorkingDocumentOptions
    ): HiveWorkingDocument;
    public reusableContent(
        contentId: string,
        branchId: string,
        reusableContentId: string,
        options?: GetDataSyncOptions & HiveWorkingDocumentOptions
    ): HiveWorkingDocument | undefined;
    public reusableContent(
        contentId: string,
        branchId: string,
        reusableContentId: string,
        options?: GetDataSyncOptions & HiveWorkingDocumentOptions
    ): HiveWorkingDocument | undefined {
        const sync = this.getSync(
            getHiveWorkingDocumentGuid({
                contentId,
                branchId,
                containerType: 'reusable-contents',
                containerId: reusableContentId,
            }),
            (guid, options) =>
                new HiveWorkingDocument(
                    this,
                    guid,
                    {
                        contentId,
                        branchId,
                        containerType: 'reusable-contents',
                        containerId: reusableContentId,
                    },
                    options
                ),
            { ...options, cache: this.cache }
        );

        if (sync && !(sync instanceof HiveWorkingDocument)) {
            throw new Error(`Expected HiveWorkingDocument, got ${sync.constructor.name}`);
        }

        return sync;
    }

    /**
     * Get or open a sync client.
     */
    private getSync(
        guid: string,
        open: (
            guid: string,
            options: GetDataSyncOptions | undefined
        ) => HiveWorkingDocument | HiveWorkingBranch,
        options: GetDataSyncOptions | undefined
    ): HiveWorkingDocument | HiveWorkingBranch | undefined {
        /**
         * Set the read-only option for all locks that are concerned by the current sync.
         */
        const applyReadOnly = (sync: HiveWorkingDocument | HiveWorkingBranch) => {
            const readOnly = options?.readOnly;
            if (readOnly === undefined) {
                return;
            }
            // Get all locks that are concerned by the current sync.
            const concernedLocks = Array.from(this.readOnlyLocks).filter((lock) =>
                lock.isAffectedSync(sync)
            );
            // If there are locks that are concerned by the current sync, set the read-only option for each of them.
            if (concernedLocks.length > 0) {
                concernedLocks.forEach((lock) => lock.syncs.set(sync, readOnly));
                return;
            }

            // Else, set the read-only option for the current sync.
            void sync.dataSync.setReadOnly(readOnly);
        };

        if (options?.autoInit === false) {
            const connection = this.pool.get(guid);
            if (connection) {
                applyReadOnly(connection);
                return connection;
            }

            return;
        }

        const connection = this.pool.get(guid, () => {
            const newConnection = open(guid, options);
            trackDataSync(newConnection);

            // Track the client ID assigned by the server.
            newConnection.dataSync.listen('clientId', (clientId) => {
                this.clientId = clientId;
            });

            return newConnection;
        });
        applyReadOnly(connection);
        return connection;
    }
}
